import { useQuery } from "@tanstack/react-query";
import {
  getSupabase,
  withTimeout,
} from "@/lib/hooks/api/utils";
import {
  buildStandardTicketsQuery,
  buildPaginatedTicketsQuery,
  CLIENT_SAFE_TICKET_FIELDS,
} from "@wtc/supabase";
import { processTicketsData } from "@/lib/utils/crm/ticket-helpers";
import { useRoleCheck } from "@/lib/hooks/auth/useRoleCheck";
import type { Ticket } from "@/lib/types/database";

/**
 * SECURITY: PostgREST `tickets` reads for embedded widgets. A `client_user` (or
 * anyone whose role hasn't resolved yet — fail-closed) must not receive
 * internal/staff-only columns over the wire, so the query selects the
 * CLIENT_SAFE_TICKET_FIELDS allowlist instead of `*`. App-layer projection only;
 * see client-safe-ticket-query.ts for the rationale + the GraphQL counterpart.
 */

export const LIVE_TICKET_QUERY_OPTIONS = {
  staleTime: 0,
  gcTime: 5 * 60 * 1000,
  refetchOnMount: true,
  refetchOnWindowFocus: true,
  refetchOnReconnect: true,
} as const;

export type TicketQueryParams = {
  page?: number;
  limit?: number;
  status?: string;
  priority?: string;
  category?: string;
  assignedTo?: string;
  search?: string;
  clientId?: string;
  critical?: boolean;
};

export function useTickets(params?: TicketQueryParams) {
  const { isEmployee } = useRoleCheck();
  const clientSafe = !isEmployee; // fail-closed: non-employees never get internal columns
  return useQuery({
    queryKey: ["tickets", { clientSafe }, params],
    ...LIVE_TICKET_QUERY_OPTIONS,
    queryFn: async () => {
      const supabase = getSupabase();
      const query = buildStandardTicketsQuery(supabase, {
        page: params?.page,
        limit: params?.limit,
        status: params?.status,
        priority: params?.priority,
        category: params?.category,
        assignedTo: params?.assignedTo,
        clientId: params?.clientId,
        critical: params?.critical,
        search: params?.search,
      }, { clientSafe });

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching tickets:", {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
          error: error,
        });
        throw new Error(error.message || "Failed to fetch tickets");
      }

      return processTicketsData(data || []);
    },
  });
}

export function useTicketsPaginated(params?: TicketQueryParams) {
  const { isEmployee } = useRoleCheck();
  const clientSafe = !isEmployee; // fail-closed: non-employees never get internal columns
  return useQuery({
    queryKey: ["tickets-paginated", { clientSafe }, params],
    ...LIVE_TICKET_QUERY_OPTIONS,
    queryFn: async () => {
      const supabase = getSupabase();
      const query = buildPaginatedTicketsQuery(supabase, {
        page: params?.page,
        limit: params?.limit,
        status: params?.status,
        priority: params?.priority,
        category: params?.category,
        assignedTo: params?.assignedTo,
        clientId: params?.clientId,
        critical: params?.critical,
        search: params?.search,
      }, { clientSafe });

      const { data, error, count } = await query;

      if (error) {
        console.error("Error fetching tickets:", {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
          error: error,
        });
        throw new Error(error.message || "Failed to fetch tickets");
      }

      return {
        data: processTicketsData(data || []),
        count: count || 0,
      };
    },
  });
}

/** Returns true if s is a valid UUID, allowing query by id; otherwise treat as ticket_number. */
function isUuid(s: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s.trim());
}

export function useTicket(ticketId: string, options?: { enabled?: boolean }) {
  const { isEmployee } = useRoleCheck();
  const clientSafe = !isEmployee; // fail-closed: non-employees never get internal columns
  return useQuery({
    queryKey: ["ticket", { clientSafe }, ticketId],
    queryFn: async () => {
      if (!ticketId || ticketId.trim() === "") {
        throw new Error("Ticket ID is required");
      }

      const supabase = getSupabase();
      // client_user reads omit internal/staff-only columns (RLS can't mask columns).
      // The select string is chosen at runtime (clientSafe ternary), so PostgREST
      // cannot statically infer the row type and falls back to GenericStringError.
      // We pin the row type at this single boundary with the typed-client's own
      // override API (see the .overrideTypes<Ticket>() below) rather than an
      // `as unknown as` cast — cf. useTickets, which launders the array type
      // through processTicketsData. NOTE: for a client_user this is a typed
      // superset — the row really contains only CLIENT_SAFE_TICKET_FIELDS at
      // runtime; absent columns read as undefined, which every consumer guards.
      const employeeTicketSelect = `
                    *,
                    contacts!inner(
                        id,
                        name,
                        company_name,
                        email
                    ),
                    time_entries(
                        id,
                        description,
                        hours,
                        entry_type,
                        billable_rate,
                        date_worked,
                        is_approved,
                        created_at
                    )
                `;
      const query = supabase
        .from("tickets")
        .select(clientSafe ? CLIENT_SAFE_TICKET_FIELDS : employeeTicketSelect);

      if (isUuid(ticketId)) {
        query.eq("id", ticketId);
      } else {
        const ticketNumber = parseInt(ticketId, 10);
        if (Number.isNaN(ticketNumber)) {
          throw new Error("Ticket identifier must be a UUID or ticket number");
        }
        query.eq("ticket_number", ticketNumber);
      }

      const { data, error } = await withTimeout(
        query.single().overrideTypes<Ticket, { merge: false }>(),
        15000,
        "ticket by id",
      );

      if (error) {
        const message =
          (error as { message?: string })?.message ||
          (typeof error === "object" && error !== null && Object.keys(error).length > 0
            ? JSON.stringify(error)
            : "Failed to fetch ticket");
        console.error("Error fetching ticket:", message, error);
        throw new Error(message);
      }

      // .single() guarantees a non-null row when `error` is null, and
      // .overrideTypes<Ticket> pins it to a non-null Ticket — no further guard.
      return data;
    },
    staleTime: 2 * 60 * 1000,
    refetchInterval: 30000,
    enabled: options?.enabled !== false && !!ticketId,
  });
}
